import _ from "lodash";

const getSafeNumber = (value, fallback = 0) => {
    return Number.isFinite(value) ? value : fallback;
};

export const getFleetUsagePercentages = (fleetMetrics = {}) => {
    return {
        capacityUsedPercentage: getSafeNumber(fleetMetrics?.capacityUsedPercentage, 0),
        securityCompliancePercentage: getSafeNumber(fleetMetrics?.securityCompliancePercentage, 0),
    };
};

export const getRoundedPercentage = (value, decimals = 0) => {
    const safe = getSafeNumber(value, 0);
    return decimals > 0 ? Number(safe.toFixed(decimals)) : Math.round(safe);
};

// Group keys as they arrive on the fleet counts payload. These are not the inventory
// SYSTEM_TYPES: storagegrid is all lowercase here, but storageGrid in the inventory URLs.
export const RESOURCE_COUNT_GROUP_KEYS = {
    ONTAP: "ontap",
    STORAGEGRID: "storagegrid",
    ESERIES: "eseries",
};

// System-level keys (ontap.systems, eseries.arrays, storagegrid.grids) are intentionally
// left out: those are surfaced by the separate systems count on the fleet summary cards.
export const RESOURCES_COUNT_GROUPS = [
    { key: RESOURCE_COUNT_GROUP_KEYS.ONTAP, countKeys: ["svms", "volumes", "luns", "namespaces", "smbshares", "qtrees", "aggregates"] },
    { key: RESOURCE_COUNT_GROUP_KEYS.STORAGEGRID, countKeys: ["tenants", "buckets"] },
    { key: RESOURCE_COUNT_GROUP_KEYS.ESERIES, countKeys: ["pools", "volumeGroups", "volumes"] },
];

const SYSTEM_COUNT_GROUPS = [
    { key: RESOURCE_COUNT_GROUP_KEYS.ONTAP, countKey: "systems" },
    { key: RESOURCE_COUNT_GROUP_KEYS.ESERIES, countKey: "arrays" },
    { key: RESOURCE_COUNT_GROUP_KEYS.STORAGEGRID, countKey: "grids" },
];

const getGroupedCount = (counts, groupKey, countKey) => {
    return getSafeNumber(_.toNumber(_.get(counts, [groupKey, countKey])), 0);
};

export const getSystemsBreakdown = (counts = {}) => {
    return _.compact(SYSTEM_COUNT_GROUPS.map(({ key, countKey }) => {
        const count = getGroupedCount(counts, key, countKey);
        return count > 0 ? { key, count } : null;
    }));
};

export const getResourcesCount = (counts = {}) => {
    return _.sumBy(RESOURCES_COUNT_GROUPS, ({ key, countKeys }) => {
        return _.sumBy(countKeys, (countKey) => getGroupedCount(counts, key, countKey));
    });
};

export const getResourcesBreakdown = (counts = {}) => {
    return _.compact(RESOURCES_COUNT_GROUPS.map(({ key, countKeys }) => {
        const rows = countKeys.map((countKey) => ({
            key: countKey,
            count: getGroupedCount(counts, key, countKey),
        }));
        const groupTotal = _.sumBy(rows, "count");

        if (groupTotal === 0) {
            return null;
        }

        return { key, rows: _.filter(rows, ({ count }) => !_.isNaN(count)) };
    }));
};

export const getCapacityColorVar = (percentage) => {
    if (percentage > 90) {
        return "var(--notification-error)";
    }

    if (percentage >= 80) {
        return "var(--notification-warning)";
    }

    return "var(--notification-success)";
};

export const getSecurityColorVar = (percentage) => {
    const badgeColor = getSecurityBadgeColor(percentage);
    return badgeColor === "orange" ? "var(--notification-warning)" : "var(--notification-success)";
};

export const getAlertColorVar = (fleetMetrics = {}) => {
    if ((fleetMetrics?.criticalAlerts ?? 0) > 0) {
        return "var(--notification-error)";
    }

    if ((fleetMetrics?.warningAlerts ?? 0) > 0) {
        return "var(--notification-warning)";
    }

    return "var(--notification-success)";
};

export const getSecurityBadgeColor = (percentage) => {
    return percentage < 100 ? "orange" : "green";
};

export const getAlertsBadgeColor = ({ selectedAlertCount = 0, fleetMetrics = {} } = {}) => {
    if (selectedAlertCount === 0) {
        return "green";
    }

    return (fleetMetrics?.criticalAlerts ?? 0) > 0 ? "red" : "orange";
};
